import { useState, useEffect } from "react";
import Dropdown from "../../components/Dropdown";
import { Button } from "../../components/Button";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getTemplates, selectTemplateById } from "../template/templateSlice";
import { appraisalState } from "./appraisalSlice";
import { allUsers } from "../user/userSlice";
import { getDepartment } from "../department/departmentSlice";
import { getAllTemplates } from "../../api/templateApi";
import { getAllUsers } from "../../api/userApi";
import { getAllDepartment } from "../../api/departmentApi";
import { createAppraisal } from "../../api/appraisalApi";
import { ArrowBackIos } from "@mui/icons-material";
import Swal from "sweetalert2";

const Appraisal = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});

  const [selectedTemplate, setSelectedTemplate] = useState({});

  const [templateOptions, setTemplateOptions] = useState();
  const [templateOptionsByDepartment, setTemplateOptionsByDepartment] =
    useState();
  const [employeeOptions, setEmployeeOptions] = useState();
  const [businessUnitOptions, setBusinessUnitOptions] = useState({});

  const [employeeAppraisalState, setEmployeeAppraisalState] = useState({
    appraisalQuestionAnswer: [],
    additionalComments: "",
  });
  const [submitReferenceState, setSubmitReferenceState] = useState(false);

  const stateOfAppraisal = useSelector(appraisalState);
  const allTemplates = useSelector(getTemplates);
  const allEmployees = useSelector(allUsers);
  const allDepartments = useSelector(getDepartment);

  useEffect(() => {
    dispatch(getAllTemplates());
    dispatch(getAllUsers());
    dispatch(getAllDepartment());
  }, [dispatch]);

  useEffect(() => {
    if (!stateOfAppraisal.error && submitReferenceState) {
      resetStates();
    }
    setSubmitReferenceState(false);
  }, [stateOfAppraisal.error, submitReferenceState]);

  const selectTemplate = useSelector((state) =>
    selectTemplateById(state, formValues?.templateId?.value)
  );

  useEffect(() => {
    const templateOptions = allTemplates?.map((template) => ({
      value: template.id,
      label: template.templateTitle,
    }));
    setTemplateOptions(templateOptions);

    const employeeOptions = allEmployees
      ?.filter((employee) => !employee.isDeleted)
      ?.map((employee) => ({
        value: employee.id,
        label: employee.firstName,
      }));
    setEmployeeOptions(employeeOptions);

    const departmentOptions = allDepartments?.map((dept) => ({
      value: dept.id,
      label: dept.departmentName,
    }));

    setBusinessUnitOptions(departmentOptions);
  }, [allTemplates, allEmployees, allDepartments]);

  useEffect(() => {
    if (formValues?.businessUnitId?.value) {
      const templateOptionsByDept = allTemplates
        ?.filter?.(
          (template) =>
            template?.businessUnit === formValues?.businessUnitId?.value
        )
        ?.map?.((template) => ({
          value: template.id,
          label: template.templateTitle,
        }));

      setTemplateOptionsByDepartment(templateOptionsByDept);
      setFormValues((prevValues) => ({ ...prevValues, templateId: null }));
      setSelectedTemplate(null);
    }
  }, [formValues?.businessUnitId, templateOptions]);

  useEffect(() => {
    if (selectTemplate) {
      const ObjectToEdit = JSON.parse(JSON.stringify(selectTemplate));
      setSelectedTemplate(ObjectToEdit);
      setEmployeeAppraisalState((prevValues) => ({
        ...prevValues,
        appraisalQuestionAnswer: [],
        additionalComments: "",
      }));
      setFormErrors({});
    }
  }, [selectTemplate]);

  useEffect(() => {
    if (Object.keys(formErrors).length !== 0) {
      if (employeeAppraisalState?.appraisalQuestionAnswer.length) {
        validateAppraisalForm();
      }
    }
  }, [employeeAppraisalState]);

  const handleDropdownChange = (name) => (option) => {
    setFormValues((prevValues) => ({ ...prevValues, [name]: option }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: option ? "" : `${name} is required`,
    }));
    setEmployeeAppraisalState((prevValues) => ({
      ...prevValues,
      [name]: option?.value,
    }));
  };

  const resetStates = () => {
    setSelectedTemplate({});
    setEmployeeAppraisalState({
      appraisalQuestionAnswer: [],
      additionalComments: "",
    });
    setFormValues({});
    setFormErrors({});
  };

  const trimObjectValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(trimObjectValues);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = trimObjectValues(obj[key]);
        return acc;
      }, {});
    } else if (typeof obj === "string") {
      return obj.trim();
    }
    return obj;
  };

  const validateAppraisalForm = () => {
    const validationErrors = {};

    if (
      !formValues?.userId ||
      formValues?.userId === "" ||
      formValues?.userId === null ||
      formValues?.userId === undefined
    ) {
      validationErrors[`userId`] = "Select employee to proceed";
    }
    if (
      !formValues?.businessUnitId ||
      formValues?.businessUnitId === "" ||
      formValues?.businessUnitId === null ||
      formValues?.businessUnitId === undefined
    ) {
      validationErrors[`businessUnitId`] = "Select business unit to proceed";
    }

    const checkBoxAnswers =
      employeeAppraisalState?.appraisalQuestionAnswer?.filter(
        (ans) => ans?.type === "checkbox"
      );

    const radioButtonAnswers =
      employeeAppraisalState.appraisalQuestionAnswer?.filter(
        (ans) => ans?.type === "radio"
      );

    selectedTemplate?.appraisalQuestions?.forEach((question, index) => {
      if (question?.optionType === 1) {
        const answersWithSameId = checkBoxAnswers?.filter(
          (ans) => ans?.questionId === question?.id
        );

        const answerExists = answersWithSameId?.some(
          (ans) => ans?.value === true
        );
        if (!answerExists) {
          validationErrors[
            `${question?.id}`
          ] = `Select an option to proceed with submission`;
        }
      } else if (question?.optionType === 2) {
        const answer = radioButtonAnswers?.find(
          (ans) => ans?.questionId === question?.id
        );

        if (!answer) {
          validationErrors[
            `${question?.id}`
          ] = `Select an option to proceed with submission`;
        } else if (answer) {
          if (answer?.type === "radio" && !("selectedOptionId" in answer)) {
            validationErrors[
              `${question?.id}`
            ] = `Select an option to proceed with submission`;
          }
        }
      }
    });

    setFormErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateAppraisalForm()) {
      const updatedItems = employeeAppraisalState.appraisalQuestionAnswer.map(
        (item) => {
          const { type, ...newItem } = item;
          return newItem;
        }
      );

      const updatedState = {
        ...employeeAppraisalState,
        appraisalQuestionAnswer: updatedItems,
      };

      dispatch(
        createAppraisal({
          editStatus: false,
          data: trimObjectValues(updatedState),
        })
      )
        .unwrap()
        .then(() => {
          setSubmitReferenceState(true);
        })
        .catch((error) => {
          console.error("Failed to update user:", error);
        });
    }
  };

  const handleCancelClick = () => {
    resetStates();
  };

  const handleRadioChange = (e, questionId, optionId) => {
    const answerExists = employeeAppraisalState?.appraisalQuestionAnswer?.find(
      (answer) => answer.questionId === questionId
    );

    const ansObject = {
      questionId: questionId,
      selectedOptionId: optionId,
      type: "radio",
      comment: answerExists?.comment || "",
    };
    const updatedRadioButtonAnswers =
      employeeAppraisalState?.appraisalQuestionAnswer?.filter(
        (ans) => ans.questionId !== ansObject.questionId
      );
    setEmployeeAppraisalState((prevValues) => ({
      ...prevValues,
      appraisalQuestionAnswer: updatedRadioButtonAnswers,
    }));
    setEmployeeAppraisalState((prevValues) => ({
      ...prevValues,
      appraisalQuestionAnswer: [
        ...prevValues.appraisalQuestionAnswer,
        ansObject,
      ],
    }));
  };

  const handleCheckBoxChange = (e, questionId, optionId) => {
    const answerExists = employeeAppraisalState?.appraisalQuestionAnswer?.find(
      (question) => question.questionId === questionId
    );

    const ansObject = {
      questionId: questionId,
      selectedOptionId: optionId,
      type: "checkbox",
      value: e.target.checked,
      comment: answerExists?.comment || "",
    };

    let removeExistingAnswerId =
      employeeAppraisalState?.appraisalQuestionAnswer;

    if (answerExists && !answerExists?.selectedOptionId) {
      removeExistingAnswerId =
        employeeAppraisalState?.appraisalQuestionAnswer?.filter(
          (ans) => ans.questionId !== questionId
        );
    }

    const updatedCheckBoxAnswers = removeExistingAnswerId
      ?.map((ans) => {
        if (
          ans.type === "checkbox" &&
          !(
            ans.questionId === questionId &&
            (ans.selectedOptionId ?? null) === ansObject.selectedOptionId
          )
        ) {
          return ans;
        }
        if (ans.type === "radio") {
          return ans;
        }
        return null;
      })
      .filter(Boolean);

    setEmployeeAppraisalState((prevValues) => {
      return {
        ...prevValues,
        appraisalQuestionAnswer: [...updatedCheckBoxAnswers, ansObject],
      };
    });
  };

  const handleInputComment = (e, type, questionId) => {
    const { value } = e.target;

    const answerExists = employeeAppraisalState?.appraisalQuestionAnswer?.some(
      (answer) => answer?.questionId === questionId
    );

    const updatedQuestions = answerExists
      ? employeeAppraisalState?.appraisalQuestionAnswer?.map((question) =>
          question.questionId === questionId
            ? { ...question, comment: value }
            : question
        )
      : [
          ...employeeAppraisalState.appraisalQuestionAnswer,
          { questionId: questionId, type: type, comment: value },
        ];

    setEmployeeAppraisalState((prevValues) => ({
      ...prevValues,
      appraisalQuestionAnswer: updatedQuestions,
    }));
  };

  return (
    <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      <div className="flex w-full justify-center items-center bg-primary">
        <button
          onClick={() => navigate("/mgr/all-appraisals")}
          className="flex w-32 pl-3 text-white cursor-pointer bg-transparent border-none outline-none"
          aria-label="Go back to all appraisals"
        >
          <ArrowBackIos /> Go Back
        </button>

        <h1 className="w-full lg:text-title-sm text-title-xsm text-center font-semibold text-white bg-primary dark:text-white py-3">
          {"Appraisal"}
        </h1>
        <div className="w-34" />
      </div>
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
          <div className="max-w-sm">
            <Dropdown
              submitRef={true}
              defaultValue={formValues?.userId || null}
              options={employeeOptions}
              handleChange={handleDropdownChange("userId")}
              errorMessage={formErrors?.userId}
              requiredField={false}
              defaultLabel="Select Employees"
              label="Employees"
              className="h-[39px]"
            />
          </div>
          <div className="max-w-sm">
            <Dropdown
              submitRef={true}
              defaultValue={formValues?.businessUnitId || null}
              options={businessUnitOptions}
              handleChange={handleDropdownChange("businessUnitId")}
              errorMessage={formErrors?.businessUnitId}
              requiredField={false}
              defaultLabel="Select Template"
              label="Business Unit"
              className="h-[39px]"
            />
          </div>
          <div className="max-w-sm">
            <Dropdown
              submitRef={true}
              defaultValue={formValues?.templateId || null}
              options={templateOptionsByDepartment}
              handleChange={handleDropdownChange("templateId")}
              errorMessage={""}
              requiredField={false}
              defaultLabel="Select Template"
              label="Templates"
              className="h-[39px]"
              message={
                !formValues?.businessUnitId &&
                "Select Business Unit to view templates"
              }
            />
          </div>
        </div>
        {selectedTemplate?.templateTitle && (
          <div className="rounded-sm  bg-white ">
            <div className="p-4">
              <div className="font-bold text-xl mb-2">
                {selectedTemplate?.templateTitle}
              </div>
              <div>
                {selectedTemplate.appraisalQuestions?.map(
                  (question, qIndex) => (
                    <div key={question.id} className="mb-5">
                      <div className="font-semibold text-base ml-2">
                        {`${qIndex + 1}. ${question.questionTitle}`}
                      </div>
                      <div className="ml-8">{question.questionSubTitle}</div>
                      <div className="mt-4 ml-12">
                        {question.questionOptions?.map(
                          (optionObj, optionIndex) => {
                            const optionValue = optionObj?.["optionTitle"];

                            return (
                              <p key={optionObj.id}>
                                <span className="font-medium mr-2">
                                  {String.fromCharCode(65 + optionIndex)}.
                                </span>
                                {question.optionType === 1 ? (
                                  <input
                                    type="checkbox"
                                    name={`option${qIndex + 1}-${
                                      optionIndex + 1
                                    }`}
                                    className="mr-2"
                                    checked={
                                      employeeAppraisalState?.appraisalQuestionAnswer?.find(
                                        (opt) =>
                                          opt?.selectedOptionId ===
                                          optionObj?.id
                                      )?.value || false
                                    }
                                    onChange={(e) =>
                                      handleCheckBoxChange(
                                        e,
                                        question?.id,
                                        optionObj?.id
                                      )
                                    }
                                  />
                                ) : (
                                  <input
                                    type="radio"
                                    className="mr-2"
                                    name={`${question.questionTitle}`}
                                    checked={
                                      employeeAppraisalState?.appraisalQuestionAnswer?.some(
                                        (opt) =>
                                          opt?.selectedOptionId ===
                                          optionObj?.id
                                      ) || false
                                    }
                                    onChange={(e) =>
                                      handleRadioChange(
                                        e,
                                        question?.id,
                                        optionObj?.id
                                      )
                                    }
                                  />
                                )}
                                {optionValue}
                              </p>
                            );
                          }
                        )}
                      </div>
                      <div
                        className={`text-xs mt-2 ml-11 h-[1em] flex transition-opacity duration-150 relative text-[#FF0000] ${
                          formErrors?.[`${question?.id}`]
                            ? "opacity-100"
                            : "opacity-0"
                        } `}
                      >
                        {formErrors?.[`${question?.id}`]}
                      </div>
                      <div className="mt-5">
                        <p>Comments</p>
                        <textarea
                          className="block p-2.5 w-full h-12 text-sm text-gray-900 bg-gray-50 rounded-lg border-[1px] border-stroke focus:ring-primary focus:border-red-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                          placeholder="Write your comments.."
                          value={
                            employeeAppraisalState?.appraisalQuestionAnswer?.find(
                              (opt) => opt?.questionId === question?.id
                            )?.comment || ""
                          }
                          onChange={(e) =>
                            handleInputComment(
                              e,
                              question.optionType,
                              question?.id
                            )
                          }
                        ></textarea>
                      </div>
                    </div>
                  )
                )}
                <div className="mt-10 border-t-2 border-stroke shadow-default">
                  <p className="mt-5">Additional Comments</p>
                  <textarea
                    className="block p-2.5 w-full h-12 text-sm text-gray-900 bg-gray-50 rounded-lg border-[1px] border-stroke focus:ring-primary focus:border-red-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder="Write your comments.."
                    value={employeeAppraisalState?.additionalComments || ""}
                    onChange={(e) => {
                      setEmployeeAppraisalState((prevValues) => ({
                        ...prevValues,
                        additionalComments: e.target.value,
                      }));
                    }}
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="flex justify-center mt-4 pb-2">
              <Button
                className="bg-red-600 mr-4"
                value="Reset the form"
                handleClick={handleCancelClick}
              />
              <Button
                disabled={stateOfAppraisal?.loading}
                value={`Submit`}
                handleClick={handleSubmit}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default Appraisal;
